import { useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../AuthContext'

export default function Header() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        setIsMenuOpen(false)
      }
    }

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isMenuOpen])

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const isSystemAdmin = user?.role === 'system_admin'

  return (
    <header className="bg-blue-600 relative">
      <div className="container mx-auto px-4 py-2 flex justify-between items-center">
        <div className="flex items-center gap-4">
          {/* Burger menu button */}
          <button
            ref={buttonRef}
            onClick={(e) => {
              e.stopPropagation()
              setIsMenuOpen(!isMenuOpen)
            }}
            className="text-white p-2 hover:bg-blue-700 rounded transition-colors"
            aria-label="Menu"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {isMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>

          <Link to="/" className="text-white text-xl font-bold hover:text-gray-200 transition-colors">
            <span className="md:hidden">ESS</span>
            <span className="hidden md:inline">Система электрических схем ESS-2</span>
          </Link>
        </div>
      </div>

      {/* Dropdown menu */}
      {isMenuOpen && (
        <div
          ref={menuRef}
          className="absolute left-4 top-full mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50"
        >
          <div className="p-4 border-b border-gray-200">
            <div className="text-sm font-semibold text-gray-900">
              {user?.name || 'User'}
            </div>
            <div className="text-xs text-gray-600 mt-1">{user?.email}</div>
            {user?.role && (
              <div className="text-xs text-gray-500 mt-1">
                Role: <span className="font-medium">{user.role}</span>
              </div>
            )}
          </div>

          <div className="py-2">
            {user && (
              <Link
                to={`/users/${user.id}`}
                onClick={() => setIsMenuOpen(false)}
                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
              >
                👤 Мой профиль
              </Link>
            )}
            <Link
              to="/comm-interface-types"
              onClick={() => setIsMenuOpen(false)}
              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
            >
              🔌 Типы интерфейсов
            </Link>
            {isSystemAdmin && (
              <Link
                to="/admin/users"
                onClick={() => setIsMenuOpen(false)}
                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
              >
                👥 Управление пользователями
              </Link>
            )}

            <button
              onClick={handleLogout}
              className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
            >
              🚪 Выйти
            </button>
          </div>
        </div>
      )}
    </header>
  )
}

